// components/navigation/SearchContext.js
// Lets the global BottomNavbar search bar adapt to the current page:
//   - pages register a placeholder (e.g. "Search Gov Glance", "Search Bills")
//   - pages may register an in-page onSearch handler; when present the bar
//     searches on the current page instead of navigating to /search
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const SearchContext = createContext(null);

export const SearchProvider = ({ children }) => {
  const [search, setSearch] = useState({
    placeholder: null,
    onSearch: null,        // (query) => void  — in-page search; null => navigate to /search
    onInputChange: null,   // (query) => void  — live typed value (optional)
    value: '',             // seed value for the input (e.g. the active query)
  });

  const registerSearch = useCallback((cfg) => {
    setSearch({
      placeholder: cfg.placeholder ?? null,
      onSearch: cfg.onSearch ?? null,
      onInputChange: cfg.onInputChange ?? null,
      value: cfg.value ?? '',
    });
  }, []);

  const resetSearch = useCallback(() => {
    setSearch({ placeholder: null, onSearch: null, onInputChange: null, value: '' });
  }, []);

  return (
    <SearchContext.Provider value={{ ...search, registerSearch, resetSearch }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => useContext(SearchContext);

// Pages call this to take over the bottom search bar while they are mounted.
// On unmount it resets, so neutral pages fall back to the default placeholder
// and the navigate-to-/search behavior.
export const usePageSearch = ({ placeholder, onSearch, onInputChange, value } = {}) => {
  const ctx = useContext(SearchContext);
  const register = ctx?.registerSearch;
  const reset = ctx?.resetSearch;
  useEffect(() => {
    if (!register) return;
    register({ placeholder, onSearch, onInputChange, value });
    return () => { if (reset) reset(); };
  }, [register, reset, placeholder, onSearch, onInputChange, value]);
};

export default SearchContext;